/**
 * Visual viewport-size picker.
 *
 * Renders the `BROWSER_VIEWPORT_PRESETS` as a keyboard-accessible radio-card
 * group (device icon + label + dimensions). Reused by the org settings section
 * and the Flow Designer "Run with config" modal.
 *
 * @module ui/components/ViewportPicker
 */

import React from 'react';
import { Radio, RadioGroup } from 'react-aria-components';
import { Desktop, DeviceMobile, DeviceTablet, Laptop } from '@phosphor-icons/react';
import { cn } from '@chatsdet/ui';
import {
  BROWSER_VIEWPORT_PRESETS,
  BROWSER_VIEWPORT_PRESET_KEYS,
  isBrowserViewportPreset,
  type BrowserViewportDevice,
  type BrowserViewportPreset,
} from '@chatsdet/types';

const ORGANIZATION_DEFAULT = '__organization_default__';

const DEVICE_ICONS: Record<BrowserViewportDevice, React.ComponentType<{ size?: number; weight?: 'regular' | 'fill' | 'duotone' }>> = {
  desktop: Desktop,
  laptop: Laptop,
  tablet: DeviceTablet,
  mobile: DeviceMobile,
};

interface ViewportPickerBaseProps {
  isDisabled?: boolean;
  /** Accessible label for the radio group. */
  'aria-label'?: string;
}

type ViewportPickerProps = ViewportPickerBaseProps &
  (
    | {
        allowOrganizationDefault: true;
        value: BrowserViewportPreset | null;
        onChange: (value: BrowserViewportPreset | null) => void;
      }
    | {
        allowOrganizationDefault?: false;
        value: BrowserViewportPreset;
        onChange: (value: BrowserViewportPreset) => void;
      }
  );

export function ViewportPicker(props: ViewportPickerProps) {
  const { value, isDisabled } = props;
  const ariaLabel = props['aria-label'] ?? 'Viewport size';
  const allowOrganizationDefault = props.allowOrganizationDefault === true;
  const selectedKey = allowOrganizationDefault && value === null ? ORGANIZATION_DEFAULT : value;
  return (
    <RadioGroup
      aria-label={ariaLabel}
      value={selectedKey}
      onChange={(next) => {
        if (props.allowOrganizationDefault) {
          props.onChange(
            next === ORGANIZATION_DEFAULT || !isBrowserViewportPreset(next) ? null : next
          );
        } else if (isBrowserViewportPreset(next)) {
          props.onChange(next);
        }
      }}
      isDisabled={isDisabled}
      className="flex flex-wrap gap-2"
    >
      {allowOrganizationDefault && (
        <Radio
          value={ORGANIZATION_DEFAULT}
          className={({ isSelected, isFocusVisible, isDisabled: itemDisabled }) =>
            cn(
              'flex w-[8.5rem] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border px-3 py-3 text-center transition-colors',
              'hover:bg-muted/40',
              isSelected
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-border bg-card',
              isFocusVisible && 'outline-none ring-2 ring-primary ring-offset-1',
              itemDisabled && 'cursor-not-allowed opacity-50'
            )
          }
        >
          <span className="text-sm font-medium text-foreground">Organization default</span>
        </Radio>
      )}
      {BROWSER_VIEWPORT_PRESET_KEYS.map((key) => {
        const preset = BROWSER_VIEWPORT_PRESETS[key];
        const Icon = DEVICE_ICONS[preset.device];
        return (
          <Radio
            key={key}
            value={key}
            className={({ isSelected, isFocusVisible, isDisabled: itemDisabled }) =>
              cn(
                'flex w-[8.5rem] cursor-pointer flex-col items-center gap-1.5 rounded-lg border px-3 py-3 text-center transition-colors',
                'hover:bg-muted/40',
                isSelected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border bg-card',
                isFocusVisible && 'outline-none ring-2 ring-primary ring-offset-1',
                itemDisabled && 'cursor-not-allowed opacity-50'
              )
            }
          >
            {({ isSelected }) => (
              <>
                <Icon
                  size={26}
                  weight={isSelected ? 'fill' : 'regular'}
                  aria-hidden
                />
                <span className="text-sm font-medium text-foreground">{preset.label}</span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {preset.width}×{preset.height}
                </span>
              </>
            )}
          </Radio>
        );
      })}
    </RadioGroup>
  );
}
